import { useStore } from "vuex";
import { ref, computed, onMounted } from "vue";

import AttachmentsEditor from "../AttachmentsEditor/AttachmentsEditor.vue";
import AttachmentsViewer from "../AttachmentsViewer/AttachmentsViewer.vue";
import FormRadioGroup from "../FormRadioGroup/FormRadioGroup.vue";
import FormCheckbox from "../FormCheckbox/FormCheckbox.vue";
import FormInputTextField from "../FormInputTextField/FormInputTextField.vue";
import FormInputTextarea from "../FormInputTextarea/FormInputTextarea.vue";

export default {
    name: "StafetMailEditorOverlay",
    components: {
        AttachmentsEditor,
        AttachmentsViewer,
        FormRadioGroup,
        FormCheckbox,
        FormInputTextField,
        FormInputTextarea
    },
    props: {
        // Props are not passed directly, but via Vuex store
        type: {
            type: String,
            required: false
        },
        mailudsendelse: {
            type: Object,
            required: false
        },
        stafetkode: {
            type: String,
            required: false
        },
        holdkode: {
            type: String,
            required: false
        },
        senderEmail: {
            type: String,
            required: false
        },
        labels: {
            type: Array,
            required: false
        },
        allowAttachments: {
            type: Boolean,
            required: false
        },
        allowSelectParticipants: {
            type: Boolean,
            required: false
        }
    },
    setup(){
        const store = useStore();
        const type = store.state.main.page_overlay_content_data.type
        const mailudsendelse = store.state.main.page_overlay_content_data.mailudsendelse
        const props = store.state.main.page_overlay_content_data[type] ?? {}
        props.labels.forEach((labelCollection) => {
            store.commit('labels/set_labelCollection', labelCollection)
        })

        const isReadonly = mailudsendelse != null
        const displayError = ref(false)
        const isSubmitting = ref(false)
        const isSubmissionError = ref(false)
        const showReceipt = ref(false)

        const recipientsDeltagere = ref(mailudsendelse?.sendToDeltagere ?? false)
        const recipientsHoldkaptajner = ref(mailudsendelse?.sendToHoldkaptajner ?? false)
        const recipientsFightere = ref(mailudsendelse?.sendToFightere ?? false)
        const seasonChoice = ref(mailudsendelse?.seasonChoice ?? null)
        const subject = ref(mailudsendelse?.subject ?? "")
        const body = ref(mailudsendelse?.body ?? "")

        // Is used for readonly checkboxes
        const trueRef = ref(true)

        const getLabel = (labelKey, params) => store.getters['labels/getLabel']({
            labelCollection: 'StafetMailEditorOverlay',
            labelKey,
            params
        })

        const heroTitle = computed(() => isReadonly
            ? getLabel('titleReadonly')
            : getLabel('title')
        )
        const heroText = computed(() => isReadonly
            ? `${getLabel('sentDateLabel')}: ${formatDateTime(mailudsendelse.sentDate)}`
            : getLabel('description')
        )

        const recipientsJoinedLabels = computed(() => {
            const recipientsLabels = []
            if (recipientsDeltagere.value) recipientsLabels.push(getLabel('recipientsDeltagereLabel'))
            if (recipientsHoldkaptajner.value) recipientsLabels.push(getLabel('recipientsHoldkaptajnerLabel'))
            if (recipientsFightere.value) recipientsLabels.push(getLabel('recipientsFightereLabel'))

            var output = ''
            if (recipientsLabels.length === 1) 
                output = recipientsLabels[0]
            else if (recipientsLabels.length === 2) 
                output = recipientsLabels.join(' og ')
            else 
                output = `${recipientsLabels.slice(0, -1).join(', ')} og ${recipientsLabels.slice(-1)}`

            return output.charAt(0).toUpperCase() + output.slice(1).toLowerCase();
        })

        const formatDateTime = (date) => {
            date = new Date(date);
            const dateStr = date.toLocaleDateString('da-DK', { year: 'numeric', month: 'short', day: 'numeric' });
            const timeStr = date.toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' });
            return `${dateStr} kl. ${timeStr}`;
        }

        const attachments = ref(
            isReadonly 
                ? mailudsendelse.attachments.map(file => ({
                    filename: file.filename,
                    url: `${document.location.origin}/${process.env.STAFET_GET_MAILUDSENDELSE_ATTACHMENT}`.replace('[attachmentId]', file.id),
                    filesize: file.filesizeBytes
                })) 
                : []
        )

        const isResendDisabled = computed(() => numNewRecipientsCount.value === null || numNewRecipientsCount.value === 0)
        const showResendConfirm = ref(false)
        const numNewRecipientsCount = ref(null)
        const isLoadingNewRecipientsCount = ref(false)
        const resendInfoText = computed(() => getLabel("resendText")?.replace("{0}", numNewRecipientsCount.value))
        
        onMounted(async () => {
            if (mailudsendelse) {
                isLoadingNewRecipientsCount.value = true
                numNewRecipientsCount.value = await store.dispatch('stafet/getMailudsendelseNewRecipientsCount', mailudsendelse.id)
                isLoadingNewRecipientsCount.value = false
            }
        })

        const seasonChoiceEnum = Object.freeze({
            Current: "Current",
            Previous: "Previous"
        })
        var seasonChoiceRadioOptions = [
            {
                value: seasonChoiceEnum.Current,
                label: getLabel('seasonChoiceCurrentLabel')
            },
            {
                value: seasonChoiceEnum.Previous,
                label: getLabel('seasonChoicePreviousLabel')
            }
        ]
        if (isReadonly) {
            // For readonly mode, only show the selected season choice
            seasonChoiceRadioOptions = seasonChoiceRadioOptions.filter(option => option.value === mailudsendelse.seasonChoice)
        }

        const bodyInputFilled = computed(() => body.value.trim().length > 0);
        const subjectInputFilled = computed(() => subject.value.trim().length > 0);
        const isRecipientsSelected = computed(() => !props.allowSelectParticipants || recipientsDeltagere.value || recipientsHoldkaptajner.value || recipientsFightere.value);
        const isSeasonSelected = computed(() => !props.allowSelectSeason || seasonChoice.value !== null);
        const isFormValid = computed(() => subjectInputFilled.value && bodyInputFilled.value && isRecipientsSelected.value && isSeasonSelected.value);

        const submit = async () => {
            
            if (isReadonly) return;

            displayError.value = false;
            isSubmissionError.value = false;

            if (!isFormValid.value) {
                displayError.value = true;
                return;
            }

            isSubmitting.value = true;

            try {
                let formData = new FormData()
                
                formData.append('type', type)
                formData.append('stafetkode', props.stafetkode)
                formData.append('holdkode', props.holdkode)
                formData.append('senderemail', props.senderEmail)

                formData.append('subject', subject.value)
                formData.append('body', body.value)

                if (props.allowSelectParticipants) {
                    formData.append('deltagere', recipientsDeltagere.value)
                    formData.append('fightere', recipientsFightere.value)
                    formData.append('holdkaptajner', recipientsHoldkaptajner.value)
                }

                if (props.allowSelectSeason) {
                    formData.append('seasonchoice', seasonChoiceToInt(seasonChoice.value))
                }

                attachments.value.forEach(file => {
                    formData.append('attachments', file)
                })

                const isSuccess = await store.dispatch('stafet/submitSendEmail', {
                    formData
                })

                if (isSuccess) {
                    showReceipt.value = true;
                }
                else {
                    isSubmissionError.value = true;
                }
            }
            catch (error) {
                console.error(error);
            }

            isSubmitting.value = false;
        }

        const seasonChoiceToInt = (value) => {
            return value == seasonChoiceEnum.Current ? 0 : 1
        }

        const openResendConfirm = () => {
            showResendConfirm.value = true
        }

        const cancelResend = () => {
            showResendConfirm.value = false
        }

        const confirmResend = async () => {
            isSubmitting.value = true

            const formData = new FormData()
            formData.append('previousMailudsendelseId', mailudsendelse.id)
            formData.append('senderemail', props.senderEmail ?? mailudsendelse.senderEmail ?? "")

            const isSuccess = await store.dispatch('stafet/submitResendEmail', {
                formData
            })
            isSubmitting.value = false

            if (isSuccess) {
                store.dispatch('main/closePageOverlay')
            } else {
                isSubmissionError.value = true
                showResendConfirm.value = false
            }
        }

        const close = () => {
            store.dispatch('main/closePageOverlay')
        }

        const reset = () => {
            showResendConfirm.value = false
            displayError.value = false
            isSubmissionError.value = false
            showReceipt.value = false
            recipientsDeltagere.value = false
            recipientsHoldkaptajner.value = false
            recipientsFightere.value = false
            seasonChoice.value = null
            subject.value = ""
            body.value = ""
            attachments.value = []
        }

        return {
            getLabel,
            isReadonly,
            heroTitle,
            heroText,
            allowSelectParticipants: props.allowSelectParticipants,
            allowSelectSeason: props.allowSelectSeason,
            allowAttachments: props.allowAttachments,
            subject,
            subjectInputFilled,
            body,
            bodyInputFilled,
            recipientsDeltagere,
            recipientsHoldkaptajner,
            recipientsFightere,
            recipientsJoinedLabels,
            isRecipientsSelected,
            seasonChoice,
            seasonChoiceRadioOptions,
            isSeasonSelected,
            attachments,
            isSubmissionError,
            isSubmitting,
            showResendConfirm,
            showReceipt,
            displayError,
            trueRef,
            submit,
            openResendConfirm,
            cancelResend,
            confirmResend,
            numNewRecipientsCount,
            isLoadingNewRecipientsCount,
            isResendDisabled,
            resendInfoText,
            close,
            reset
        }
    }
}